import { css, Interpolation } from '@emotion/react';
import {
  Button,
  CellValues,
  ColumnHeading,
  Dialog,
  EntityFilterControlsConfig,
  Image,
  PaginatedDataTable,
  TableCellStylesFn,
  Text,
  Theme,
  useVisibleHeadings,
} from '@lbcamden/lib-react';
import { formatDMY } from '@lbcamden/shared-utils';
import { FunctionComponent, useCallback, useState } from 'react';
import { useNavigate } from 'react-router';

import { SectionContainer } from '~/client/components/common/SectionContainer';
import { serviceRequestStatusDisplayValues } from '~/client/config/display-values/serviceRequests';
import { useServiceRequestsList } from '~/client/hooks/storeHooks';
import { toServiceRequestPage } from '~/client/utils/navigation';
import { ServiceRequest } from '~/common/types/serviceRequests';
import { selectOptionsFromDisplayValues } from '~/common/utils/data';

type Props = {
  buildingId: string;
  propertyId: number;
};

export type ColumnId = StringKey<ServiceRequest>;

const headings = [
  { id: 'reportedDate', content: 'Reported' },
  { id: 'description', content: 'Description' },
  { id: 'isPropertyRequest', content: 'Type' },
  { id: 'status', content: 'Status' },
  { id: 'modifiedDate', content: 'Last updated' },
] as const satisfies ColumnHeading<ColumnId>[];

const filterConfigs: EntityFilterControlsConfig<ServiceRequest> = [
  [
    {
      type: 'toggle',
      key: 'isPropertyRequest',
      label: 'Dwelling',
      activeValue: true,
    },
    {
      type: 'toggle',
      key: 'isPropertyRequest',
      label: 'Communal',
      activeValue: false,
    },
  ],
  {
    type: 'multi-select',
    key: 'status',
    label: 'Status',
    options: selectOptionsFromDisplayValues(serviceRequestStatusDisplayValues),
  },
];

const mapServiceRequestToRow = (serviceRequest: ServiceRequest): CellValues<ColumnId> => ({
  ...serviceRequest,
  _rowId: String(serviceRequest.serviceRequestId),
  isPropertyRequest: serviceRequest.isPropertyRequest ? 'Dwelling' : 'Communal',
  reportedDate: formatDMY(serviceRequest.reportedDate),
  modifiedDate: serviceRequest.modifiedDate && formatDMY(serviceRequest.modifiedDate),
});

const cellStyles: TableCellStylesFn<ColumnId> = (columnId) => (theme) => ({
  width: columnId === 'description' ? 200 : 50,
  maxWidth: columnId === 'description' ? 'auto' : 70,
  [theme.breakpoints.down('md')]: {
    width: 'auto',
  },
});

const buttonStyle: Interpolation<Theme> = (theme) => css`
  display: inline;
  color: ${theme.colors.primary};
  text-decoration: underline;
  border: 0;

  &:hover,
  &:focus,
  &:active {
    color: ${theme.colors.primaryHover};
  }
`;

export const ServiceRequests: FunctionComponent<Props> = ({ propertyId, buildingId }) => {
  const navigate = useNavigate();
  const serviceRequests = useServiceRequestsList(propertyId, buildingId);
  const [showQRCode, setShowQRCode] = useState(false);

  const visibleHeadings = useVisibleHeadings(headings, {
    xs: [headings[0], headings[2], headings[1]],
    sm: [headings[0], headings[2], headings[1]],
  });

  const onClickRow = useCallback(
    ({ serviceRequestId }: ServiceRequest) =>
      navigate(toServiceRequestPage(propertyId, serviceRequestId)),
    [],
  );

  return (
    <SectionContainer title="Recent or outstanding repairs">
      <PaginatedDataTable
        cellStyles={cellStyles}
        headings={visibleHeadings}
        filterConfigs={filterConfigs}
        items={serviceRequests}
        defaultSort={{ field: 'reportedDate', direction: 'desc' }}
        mapItemToRow={mapServiceRequestToRow}
        onClickRow={onClickRow}
      />

      <Text css={(theme) => ({ marginTop: theme.spacing(3) })}>
        To raise a new repair request, help the resident navigate to{' '}
        <a href="https://housing-repairs.camden.gov.uk/" target="_blank" rel="noreferrer">
          https://housing-repairs.camden.gov.uk
        </a>
        , or show them this{' '}
        <Button css={buttonStyle} $variant="link" onClick={() => setShowQRCode(true)}>
          QR Code
        </Button>
      </Text>
      <Dialog
        accessibleInfo={{
          title: 'New repair request',
          description: 'To raise a new repair request, scan this QR Code',
        }}
        open={showQRCode}
        onClose={() => setShowQRCode(false)}
      >
        <Image src="/images/service-requests/new-repair-request-qrcode.png" />
      </Dialog>
    </SectionContainer>
  );
};
